import { ExternalLink } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

import { useAccountDetail, useAccountSessions } from "../api";
import { CHART_COLORS } from "./dashboard/colors";
import { fmtCur, fmtMRR, monthLabel } from "../format";

import { AccountLinksSection } from "./account-links/AccountLinksSection";

// ARR tier bands — must match the tiers feature's thresholds.
const TIER_BANDS_ARR = [
  { tier: 1, min: 200_000 },
  { tier: 2, min: 85_000 },
  { tier: 3, min: 25_000 },
  { tier: 4, min: 10_000 },
  { tier: 5, min: 0 },
];

function tierForArr(arr: number): number | null {
  if (!arr || arr <= 0) return null;
  for (const b of TIER_BANDS_ARR) if (arr >= b.min) return b.tier;
  return null;
}

/**
 * The expanded-mode account view. Renders inside the EntityDrawer when the
 * pipeline URL carries `?account=<qbId>&expanded=1`. Prop-driven so it doesn't
 * depend on a specific route — the same component would work mounted anywhere
 * a qbId is in scope.
 */
export function AccountDetailContent({ qbId }: { qbId: number }) {
  const q = useAccountDetail(qbId);

  if (q.isLoading) {
    return <p className="p-5 text-sm text-muted-foreground">Loading account…</p>;
  }
  if (q.error) {
    return <p className="p-5 text-sm text-destructive">{String(q.error)}</p>;
  }
  if (!q.data) {
    return <p className="p-5 text-sm text-muted-foreground">No data.</p>;
  }

  const { pipeline_row, summary, contracts, companies, history, annotation_log, account_links } = q.data;

  return (
    <div className="flex flex-col gap-6 p-5">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight">
          {pipeline_row?.account_name ?? (summary as { account_name?: string })?.account_name ?? `QB-${qbId}`}
        </h1>
        <p className="text-sm text-muted-foreground">QB-{qbId}</p>
      </div>

      {pipeline_row && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="text-xs uppercase">Status</CardDescription>
              <CardTitle className="text-xl">{pipeline_row.billing_status}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="text-xs uppercase">Current ARR</CardDescription>
              <CardTitle className="text-xl">{fmtCur(pipeline_row.current_arr)}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="text-xs uppercase">Tier</CardDescription>
              <CardTitle className="text-xl">
                {pipeline_row.current_tier ? `T${pipeline_row.current_tier}` : "—"}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="text-xs uppercase">Renewal</CardDescription>
              <CardTitle className="text-xl">{monthLabel(pipeline_row.renewal_month)}</CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Contracts</CardTitle>
          <CardDescription>{contracts.length} contracts on this account</CardDescription>
        </CardHeader>
        <CardContent className="overflow-auto p-0">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-2 py-2">Type</th>
                <th className="px-2 py-2">Signed</th>
                <th className="px-2 py-2">Effective</th>
                <th className="px-2 py-2">Term</th>
                <th className="px-2 py-2">MRR</th>
                <th className="px-2 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((c, i) => {
                // pandadoc_id rides along in the account-detail payload (selected
                // in _ACCOUNT_CONTRACTS_SQL). When present, link the contract
                // type to the signed document in PandaDoc — same deep-link the
                // legacy employee portal uses.
                const pandadocId = c.pandadoc_id ? String(c.pandadoc_id) : null;
                return (
                <tr key={i} className="border-t border-border">
                  <td className="px-2 py-1.5">
                    {pandadocId ? (
                      <a
                        href={`https://app.pandadoc.com/a/#/documents/${pandadocId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-medium text-brand hover:underline"
                        title="Open the signed contract in PandaDoc"
                      >
                        {String(c.contract_type_name ?? "Contract")}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      String(c.contract_type_name ?? "")
                    )}
                  </td>
                  <td className="px-2 py-1.5">{String(c.contract_signed_at ?? "")}</td>
                  <td className="px-2 py-1.5">
                    {String(c.effective_from ?? "")} → {String(c.effective_through ?? "—")}
                  </td>
                  <td className="px-2 py-1.5">{String(c.term_months ?? "")}</td>
                  <td className="px-2 py-1.5">{fmtMRR(Number(c.mrr_total))}</td>
                  <td className="px-2 py-1.5">{String(c.contract_state ?? "")}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Companies</CardTitle>
          <CardDescription>{companies.length} companies linked to this account</CardDescription>
        </CardHeader>
        <CardContent className="overflow-auto p-0">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-2 py-2">Name</th>
                <th className="px-2 py-2">City</th>
                <th className="px-2 py-2">State</th>
                <th className="px-2 py-2">Active</th>
                <th className="px-2 py-2">Billed first</th>
                <th className="px-2 py-2">Billed last</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-2 py-1.5">{String(c.name ?? "")}</td>
                  <td className="px-2 py-1.5">{String(c.city ?? "")}</td>
                  <td className="px-2 py-1.5">{String(c.state ?? "")}</td>
                  <td className="px-2 py-1.5">{c.is_active ? "Yes" : "No"}</td>
                  <td className="px-2 py-1.5">{String(c.billed_first_date ?? "")}</td>
                  <td className="px-2 py-1.5">{String(c.billed_last_date ?? "")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Billing history</CardTitle>
          <CardDescription>
            {history.length} months · subscription ARR (MRR × 12) with tier bands and
            tier-change markers, plus usage billing as its own annualized line.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No billing history.
            </p>
          ) : (
            <MrrChart data={history} />
          )}
          <details className="mt-3 rounded-md border border-border bg-muted/30 p-3 text-sm">
            <summary className="cursor-pointer font-medium">
              Show monthly billing table
            </summary>
            <div className="mt-3 overflow-auto">
              <table className="w-full text-xs">
                <thead className="text-left uppercase text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5">Period</th>
                    <th className="px-2 py-1.5 text-right">Subscription MRR</th>
                    <th className="px-2 py-1.5 text-right">Usage</th>
                    <th className="px-2 py-1.5 text-right">ARR</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.period} className="border-t border-border">
                      <td className="px-2 py-1.5 font-mono">{h.period}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmtMRR(h.mrr)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {h.usage != null ? fmtCur(h.usage) : "—"}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {fmtCur((h.mrr ?? 0) * 12)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </CardContent>
      </Card>

      <AccountLinksSection qbId={qbId} links={account_links} />

      <RenewalCyclesSection qbId={qbId} />


      {annotation_log.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Annotation history</CardTitle>
            <CardDescription>Every CSM-authored change, newest first.</CardDescription>
          </CardHeader>
          <CardContent className="overflow-auto p-0">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-2 py-2">When</th>
                  <th className="px-2 py-2">Field</th>
                  <th className="px-2 py-2">From</th>
                  <th className="px-2 py-2">To</th>
                  <th className="px-2 py-2">By</th>
                </tr>
              </thead>
              <tbody>
                {annotation_log.map((h) => (
                  <tr key={h.id} className="border-t border-border">
                    <td className="px-2 py-1.5 font-mono text-xs">
                      {h.changed_at?.slice(0, 19).replace("T", " ")}
                    </td>
                    <td className="px-2 py-1.5">{h.field_label ?? h.field}</td>
                    <td className="px-2 py-1.5">{h.old_value ?? "—"}</td>
                    <td className="px-2 py-1.5">{h.new_value ?? "—"}</td>
                    <td className="px-2 py-1.5">{h.changed_by_name ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/** Subscription ARR over time with tier-band lines + tier-change markers,
 *  plus usage billing as a separate annualized line (tooltip shows the
 *  monthly amount). */
function MrrChart({
  data,
}: {
  data: Array<{ period: string; mrr: number; usage?: number }>;
}) {
  let prevTier: number | null = null;
  const series = data.map((d) => {
    const mrr = Number(d.mrr) || 0;
    const usage = Number(d.usage) || 0;
    const arr = mrr * 12;
    const tier = tierForArr(arr);
    let tier_change: string | null = null;
    if (tier != null && prevTier != null && tier !== prevTier) {
      tier_change = `T${prevTier} → T${tier}`;
    }
    if (tier != null) prevTier = tier;
    return { period: d.period, mrr, usage, usage_annual: usage * 12, arr, tier, tier_change };
  });
  const hasUsage = series.some((d) => d.usage > 0);

  const max = Math.max(
    ...series.map((d) => d.arr),
    ...(hasUsage ? series.map((d) => d.usage_annual) : [0]),
    0,
  );
  const yMax = Math.ceil((max * 1.1) / 5000) * 5000 || 10000;
  const tickInterval = Math.max(0, Math.floor(series.length / 6) - 1);
  const tierChanges = series.filter((d) => d.tier_change != null);
  const visibleBands = TIER_BANDS_ARR.filter((b) => b.min > 0 && b.min <= yMax);

  return (
    <div className="h-64">
      <ResponsiveContainer>
        <AreaChart data={series} margin={{ top: 16, right: 28, bottom: 6, left: 8 }}>
          <defs>
            <linearGradient id="arrFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CHART_COLORS.success} stopOpacity={0.2} />
              <stop offset="100%" stopColor={CHART_COLORS.success} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={CHART_COLORS.gridLine} strokeDasharray="2 3" vertical={false} />
          <XAxis
            dataKey="period"
            tick={{ fontSize: 10 }}
            tickLine={false}
            interval={tickInterval}
            minTickGap={32}
          />
          <YAxis
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => fmtCur(Number(v))}
            width={66}
            domain={[0, yMax]}
          />
          <Tooltip content={<MrrTooltip />} cursor={{ stroke: CHART_COLORS.muted, strokeDasharray: "3 3" }} />
          {visibleBands.map((b) => (
            <ReferenceLine
              key={`tier-${b.tier}`}
              y={b.min}
              stroke={CHART_COLORS.muted}
              strokeDasharray="2 4"
              strokeOpacity={0.6}
              label={{ value: `T${b.tier}`, position: "left", fontSize: 10 }}
            />
          ))}
          <Area
            type="monotone"
            dataKey="arr"
            stroke={CHART_COLORS.success}
            strokeWidth={2}
            fill="url(#arrFill)"
            dot={false}
            activeDot={{ r: 4, fill: CHART_COLORS.success, stroke: CHART_COLORS.background, strokeWidth: 2 }}
          />
          {hasUsage && (
            <Area
              type="monotone"
              dataKey="usage_annual"
              stroke={CHART_COLORS.brand}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              fill="none"
              dot={false}
              activeDot={{ r: 3, fill: CHART_COLORS.brand, stroke: CHART_COLORS.background, strokeWidth: 2 }}
            />
          )}
          {tierChanges.map((d, i) => (
            <ReferenceDot
              key={`tc-${i}`}
              x={d.period}
              y={d.arr}
              r={5}
              fill={CHART_COLORS.power}
              stroke={CHART_COLORS.background}
              strokeWidth={2}
              label={{
                value: d.tier_change ?? "",
                position: "top",
                fontSize: 9,
                offset: 6,
              }}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

interface MrrTooltipPayload {
  payload: {
    period: string;
    mrr: number;
    usage: number;
    arr: number;
    tier: number | null;
    tier_change: string | null;
  };
}

/** Past + current renewal cycles for the account, newest first.
 *  Current cycle is highlighted; closed cycles surface an outcome chip. */
function RenewalCyclesSection({ qbId }: { qbId: number }) {
  const q = useAccountSessions(qbId);
  const sessions = q.data?.sessions ?? [];
  if (q.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Renewal cycles</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading cycles…</p>
        </CardContent>
      </Card>
    );
  }
  if (sessions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Renewal cycles</CardTitle>
          <CardDescription>
            One row per anniversary. Anchor this account in the quick view to
            start tracking.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No cycles recorded yet.
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Renewal cycles ({sessions.length})</CardTitle>
        <CardDescription>
          One row per anniversary, newest first. Outcomes are computed by the
          daily rollover job from billing history.
        </CardDescription>
      </CardHeader>
      <CardContent className="overflow-auto p-0">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-2 py-2">Period</th>
              <th className="px-2 py-2">Forecast</th>
              <th className="px-2 py-2">Outcome</th>
              <th className="px-2 py-2 text-right">ARR Δ</th>
              <th className="px-2 py-2">Status</th>
              <th className="px-2 py-2">Notes</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s, idx) => {
              const isCurrent = idx === 0 && s.closed_at === null;
              return (
                <tr
                  key={s.id}
                  className={
                    isCurrent
                      ? "border-t-2 border-brand bg-brand/5"
                      : "border-t border-border"
                  }
                >
                  <td className="px-2 py-1.5 font-medium">
                    {monthLabel(s.renewal_period)}
                    {isCurrent && (
                      <span className="ml-2 rounded-full bg-brand px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
                        Current
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">{s.forecast}</td>
                  <td className="px-2 py-1.5">
                    {s.outcome ? <OutcomeBadge outcome={s.outcome} /> : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {s.outcome_arr_delta == null
                      ? "—"
                      : fmtCur(s.outcome_arr_delta)}
                  </td>
                  <td className="px-2 py-1.5">{s.renewal_status}</td>
                  <td className="max-w-[24rem] truncate px-2 py-1.5 text-muted-foreground">
                    {s.notes ?? ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function OutcomeBadge({
  outcome,
}: {
  outcome: "renewed" | "churned" | "expansion" | "contraction";
}) {
  const variant =
    outcome === "renewed"
      ? "success"
      : outcome === "churned"
        ? "danger"
        : outcome === "expansion"
          ? "success-solid"
          : "warning";
  return <Badge variant={variant}>{outcome}</Badge>;
}

function MrrTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: MrrTooltipPayload[];
  label?: string;
}) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-md">
      <div className="text-muted-foreground">{label}</div>
      <div className="font-semibold text-success">{fmtCur(p.arr)} ARR</div>
      <div className="text-muted-foreground">{fmtMRR(p.mrr)}/mo subscription</div>
      {p.usage > 0 && (
        <div className="font-medium text-brand">{fmtCur(p.usage)}/mo usage</div>
      )}
      {p.tier != null && (
        <div className="mt-1 text-muted-foreground">
          Tier <b className="text-foreground">T{p.tier}</b>
          {p.tier_change && (
            <span className="ml-2 font-semibold text-info">{p.tier_change}</span>
          )}
        </div>
      )}
    </div>
  );
}
